
export function extractDocumentCharacters(value: string | null | undefined): string {
  return String(value ?? '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^A-Za-z0-9]/g, '')
    .toUpperCase();
}

/** Máscara do b3-input-text que permite digitar CPF (11) ou CNPJ (14) sem travar no 11º dígito. */
export function resolveCpfCnpjInputMask(value: string | null | undefined): 'text' | 'cnpj' {
  const alnum = extractDocumentCharacters(value);
  if (/[A-Z]/.test(alnum)) {
    return 'text';
  }

  return alnum.length >= 12 ? 'cnpj' : 'text';
}

export function getDocumentCharacterCount(value: string | null | undefined): number {
  return extractDocumentCharacters(value).length;
}

/** Formata CPF/CNPJ para exibição com pontos e traços quando o documento está completo. */
export function formatCpfCnpjInputValue(
  raw: string | null | undefined,
  formatWhenComplete = true,
): string {
  const alnum = extractDocumentCharacters(raw);
  if (!alnum) {
    return '';
  }

  if (!formatWhenComplete) {
    return alnum;
  }

  if (/[A-Z]/.test(alnum)) {
    return alnum.length === 14 ? formatCNPJ(alnum) : alnum;
  }

  if (alnum.length === 11) {
    return formatCPF(alnum);
  }

  if (alnum.length === 14) {
    return formatCNPJ(alnum);
  }

  return alnum;
}

export function detectDocumentType(value: string): 'CPF' | 'CNPJ' | 'UNKNOWN' {
  // Normaliza a string para remover acentos
  const valorNormalizado = value.normalize('NFD').replace(/[\u0300-\u036f]/g, '');

  // Remove pontos (.), barras (/) e traços (-)
  const cleanedValue = valorNormalizado.replace(/[./-]/g, '');

  const hasNonNumeric = /\D/.test(cleanedValue);

  if (hasNonNumeric) {
    return 'CNPJ';
  }

  if (cleanedValue.length === 11 && !hasNonNumeric) {
    return 'CPF';
  } else if (cleanedValue.length > 11 && cleanedValue.length <= 14) {
    return 'CNPJ';
  } else {
    return 'UNKNOWN';
  }
}

export function formatCPFOrCNPJ(value: string): string {
  // Normaliza e remove acentos
  const valorNormalizado = value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim();

  // Mantém somente letras e números (A-Z, a-z, 0-9)
  const cleanedValue = valorNormalizado.replace(/[^A-Za-z0-9]/g, '');



  if (isValidCPF(cleanedValue)) {
    return formatCPF(cleanedValue);
  } else if (isValidCNPJ(cleanedValue)) {
    return formatCNPJ(cleanedValue);
  } else {
    // Retorna o valor original se não for CPF nem CNPJ válido
    return value;
  }
}

export function isValidCPF(cpf: string): boolean {
  if (cpf.length !== 11 || /[./-]/g.test(cpf)) {
    return false;
  }

  return true;
}

export function isValidCNPJ(cnpj: string): boolean {
  if (cnpj.length !== 14 || /[./-]/g.test(cnpj)) {
    return false;
  }

  return true;
}

export function formatCPF(cpf: string): string {
  // Verifica se já está no formato correto
  const cpfPattern = /^\d{3}\.\d{3}\.\d{3}-\d{2}$/;
  if (cpfPattern.test(cpf)) {
    return cpf;
  }

  // Aplica a máscara
  return cpf.replace(/^(\d{3})(\d{3})(\d{3})(\d{2})$/, '$1.$2.$3-$4');
}

export function formatCNPJ(cnpj: string): string {
  if (cnpj == null) return '';

  // Normaliza, remove acentos e trim
  const normalized = String(cnpj)
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim();

  // 1) Se JÁ está no formato correto aceitando letras/números, retorna como está
  const cnpjAlphaFormatted =
    /^[A-Za-z0-9]{2}\.[A-Za-z0-9]{3}\.[A-Za-z0-9]{3}\/[A-Za-z0-9]{4}-[A-Za-z0-9]{2}$/;

  if (cnpjAlphaFormatted.test(normalized)) {
    return normalized.toUpperCase(); // opcional: padroniza maiúsculas
  }

  // 2) Mantém apenas caracteres alfanuméricos e limita a 14
  const alnum = normalized.replace(/[^A-Za-z0-9]/g, '').toUpperCase();
  if (alnum.length !== 14) {
    // Se não tem 14 caracteres, não formata (mantém comportamento original)
    return cnpj;
  }

  // 3) Aplica a "máscara" alfanumérica: XX.XXX.XXX/XXXX-XX
  return formatCNPJProgressive(alnum);
}

/** Aplica máscara progressiva de CNPJ alfanumérico (XX.XXX.XXX/XXXX-XX), limitado a 14 chars. */
export function formatCNPJProgressive(value: string | null | undefined): string {
  const alnum = extractDocumentCharacters(value).slice(0, 14);
  if (!alnum) {
    return '';
  }

  let result = alnum.slice(0, 2);
  if (alnum.length > 2) {
    result += `.${alnum.slice(2, Math.min(5, alnum.length))}`;
  }
  if (alnum.length > 5) {
    result += `.${alnum.slice(5, Math.min(8, alnum.length))}`;
  }
  if (alnum.length > 8) {
    result += `/${alnum.slice(8, Math.min(12, alnum.length))}`;
  }
  if (alnum.length > 12) {
    result += `-${alnum.slice(12, Math.min(14, alnum.length))}`;
  }

  return result;
}

export function removeNonDigitsCPF(value: string): string {
  const cleanedValue = value.replace(/\D/g, '');
  return cleanedValue;
}

export function removeNonDigitsCNPJ(value: string): string {
  return extractDocumentCharacters(value);
}

